// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import { isAxiosError } from "axios"
import { apiClient } from "@/queries/client"

// Wire is snake_case (PRD §Invariants INV-6). Both hooks share the `apiClient`
// (single instance, request-interceptor auth, central 401 redirect — ADR-0011).
// Query keys embed the params so cached variants don't collide.

export type ListAgentsParams = {
  role?: "evolver" | "meta" | "domain"
  parent_evolver_id?: string
  parent_meta_agent_id?: string
  origin_evolution_cycle_id?: string
  include_all?: boolean
}

// Skips absent/empty values — keeps the bookmarkable URL clean (ADR-0006).
const buildListParams = (params: ListAgentsParams | undefined) => {
  const out: Record<string, string> = {}
  if (params?.role) out.role = params.role
  if (params?.parent_evolver_id && params.parent_evolver_id.trim().length > 0) {
    out.parent_evolver_id = params.parent_evolver_id.trim()
  }
  if (
    params?.parent_meta_agent_id &&
    params.parent_meta_agent_id.trim().length > 0
  ) {
    out.parent_meta_agent_id = params.parent_meta_agent_id.trim()
  }
  if (
    params?.origin_evolution_cycle_id &&
    params.origin_evolution_cycle_id.trim().length > 0
  ) {
    out.origin_evolution_cycle_id = params.origin_evolution_cycle_id.trim()
  }
  if (params?.include_all === true) out.include_all = "true"
  return out
}

// `next_cursor` is always null in v1 — cursor pagination is scaffolded only
// (PRD §Out of Scope). `opts.enabled` lets wrappers mount without firing until
// their preconditions hold (e.g. `useDescendants` waits for a resolved agentId).
export const useAgents = (
  params?: ListAgentsParams,
  opts?: { enabled?: boolean },
): UseQueryResult<AgentListResponse, Error> => {
  const queryParams = buildListParams(params)
  return useQuery<AgentListResponse, Error>({
    queryKey: ["agents", "list", queryParams],
    enabled: opts?.enabled ?? true,
    staleTime: 1000 * 60,
    queryFn: async () => {
      const response = await apiClient.get<AgentListResponse>(
        "/front-api/agents",
        { params: queryParams },
      )
      return response.data
    },
  })
}

// Only evolver and meta agents produce descendants (PRD §Glossary): domain/null
// roles leave the hook disabled — no fetch issued. The two roles map to different
// filter params (`parent_evolver_id` vs `parent_meta_agent_id`).
export const useDescendants = (
  agentId: string | undefined,
  role: string | null | undefined,
): UseQueryResult<AgentListResponse, Error> => {
  const params: ListAgentsParams =
    role === "evolver"
      ? { parent_evolver_id: agentId }
      : role === "meta"
        ? { parent_meta_agent_id: agentId }
        : {}
  const enabled =
    (role === "evolver" || role === "meta") &&
    typeof agentId === "string" &&
    agentId.length > 0
  return useAgents(params, { enabled })
}

// 404 resolves to null rather than rejecting: `OperatorRow` calls this hook to
// resolve a `parent_meta_agent_id` that may have been purged — throwing would
// trigger TanStack Query's default retry loop (dashboard-audit-fixes US §23).
// Any non-404 failure still rejects so genuine outages surface as `isError`.
export const useAgent = (
  agentId: string | undefined,
): UseQueryResult<AgentDetails | null, Error> => {
  return useQuery<AgentDetails | null, Error>({
    queryKey: ["agents", "detail", agentId],
    enabled: typeof agentId === "string" && agentId.length > 0,
    staleTime: 1000 * 60,
    queryFn: async () => {
      try {
        const response = await apiClient.get<AgentDetails>(
          `/front-api/agents/${encodeURIComponent(agentId as string)}`,
        )
        return response.data
      } catch (error: unknown) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
  })
}
